import {Machine} from 'xstate';
import {Sender} from 'xstate/lib/types';
import {LongTimeoutResult} from '@joyrideautos/auction-utils/src/setLongTimeout';
import {
    CHECK_ITEM_BIDDING_ENDED_INTERVAL,
    ENDED_RECENTLY_TIME_IN_MS,
    ListingAuctionStateEnum,
    ListingAuctionStateMachineContext,
    ListingAuctionStateMachineEvent,
    ListingAuctionStateMachineSchema,
} from './types';
import {reaction, when} from 'mobx';
import {
    executeAt,
    startCountDown,
    subscribeToAuctionPauseChange,
    subscribeToAuctionStartChange,
    subscribeToClockOffsetChange,
} from './utils';
import {logError} from '@joyrideautos/ui-logger/src/utils';
import makeLogger, {LoggerStatusEnum} from '@joyrideautos/ui-logger/src/Logger';

const logger = makeLogger()('ListingAuctionStateMachine', LoggerStatusEnum.ENABLED);

const pauseStateInvocation =
    (context: ListingAuctionStateMachineContext) => (callback: Sender<ListingAuctionStateMachineEvent>) => {
        const auctionPauseChangeDisposer = subscribeToAuctionPauseChange(context, (paused) => {
            if (!paused) {
                callback('RESUME');
            }
        });

        return () => {
            auctionPauseChangeDisposer && auctionPauseChangeDisposer();
        };
    };

export const listingAuctionStateMachine = Machine<
    ListingAuctionStateMachineContext,
    ListingAuctionStateMachineSchema,
    ListingAuctionStateMachineEvent
>(
    {
        id: 'listingAuction',
        initial: ListingAuctionStateEnum.BEFORE_START,
        on: {
            SUSPEND: ListingAuctionStateEnum.SUSPENDED,
        },
        states: {
            beforeStart: {
                invoke: {
                    src:
                        (context: ListingAuctionStateMachineContext) =>
                        (callback: Sender<ListingAuctionStateMachineEvent>) => {
                            const {presenter} = context;
                            if (presenter.auction.ended && presenter.areAllResultsEnded) {
                                callback('ENDED_RECENTLY');
                                return;
                            }

                            const disposers: (() => void)[] = [];
                            let timerDisposer: LongTimeoutResult | undefined = undefined;
                            let countDownDisposer: (() => void) | undefined;

                            const checkBeforeStartStateEnded = () => {
                                timerDisposer && timerDisposer.clearTimeout();

                                const disposer = executeAt(() => callback('START_BIDDING'), presenter.startAuctionTime);

                                if (!disposer) {
                                    callback('START_BIDDING');
                                } else {
                                    logger.log(
                                        `${new Date()}: start auction at ${new Date(presenter.startAuctionTime)}`
                                    );
                                }

                                return disposer;
                            };

                            const startBeforeStartStateCountDown = () => {
                                countDownDisposer && countDownDisposer();
                                return startCountDown(presenter, presenter.startAuctionRemainderInMs);
                            };

                            timerDisposer = checkBeforeStartStateEnded();
                            countDownDisposer = startBeforeStartStateCountDown();

                            disposers.push(
                                subscribeToClockOffsetChange(presenter.clock)(() => {
                                    timerDisposer = checkBeforeStartStateEnded();
                                    countDownDisposer = startBeforeStartStateCountDown();
                                })
                            );

                            disposers.push(
                                subscribeToAuctionStartChange(context, () => {
                                    timerDisposer = checkBeforeStartStateEnded();
                                })
                            );

                            return () => {
                                timerDisposer && timerDisposer.clearTimeout();
                                countDownDisposer && countDownDisposer();
                                disposers.forEach((d) => d());
                            };
                        },
                },
                on: {
                    START_BIDDING: [
                        {target: ListingAuctionStateEnum.EMPTY, cond: 'isEmpty'},
                        {target: ListingAuctionStateEnum.SUSPENDED, cond: 'isSuspended'},
                        {target: ListingAuctionStateEnum.BIDDING, cond: 'isAuctionStarted'},
                        {target: ListingAuctionStateEnum.ENDED_RECENTLY},
                    ],
                    ENDED_RECENTLY: ListingAuctionStateEnum.ENDED_RECENTLY,
                },
            },
            bidding: {
                invoke: {
                    src:
                        ({presenter, services}: ListingAuctionStateMachineContext) =>
                        (callback: Sender<ListingAuctionStateMachineEvent>) => {
                            logger.log('Bidding State Entered', {
                                earliestItemEndTime: new Date(presenter.earliestItemEndTime).toISOString(),
                                latestItemEndTime: new Date(presenter.latestItemEndTime).toISOString(),
                            });
                            if (
                                !presenter.latestWinningBid &&
                                presenter.auction.settings.isContinuousAuctionEnabled &&
                                !presenter.auction.ended
                            ) {
                                const endAuctionTimeDisposer = executeAt(
                                    () => callback('ENDED_RECENTLY'),
                                    presenter.endAuctionTime
                                );
                                if (!endAuctionTimeDisposer) {
                                    callback('ENDED_RECENTLY');
                                    return;
                                }

                                const checkBiddingEndedDisposer = reaction(
                                    () => presenter.latestWinningBid,
                                    (earliestWinningBid) => {
                                        if (earliestWinningBid) {
                                            endAuctionTimeDisposer.clearTimeout();
                                            callback('START_BIDDING');
                                        }
                                    }
                                );

                                return () => {
                                    endAuctionTimeDisposer.clearTimeout();
                                    checkBiddingEndedDisposer();
                                };
                            }

                            let checkBiddingEndedTimerDisposer: LongTimeoutResult | undefined;
                            let checkItemBiddingEndedPollingDisposer: (() => void) | undefined;
                            let checkItemBiddingEndedTimerDisposer: LongTimeoutResult | undefined;
                            let auctionCountDownDisposer: (() => void) | undefined;

                            if (presenter.earliestWinningBid) {
                                logger.log(
                                    `[${new Date().toISOString()}] schedule initial check item bidding ended for`,
                                    presenter.earliestWinningBid.itemKey,
                                    new Date(presenter.earliestItemEndTime).toISOString()
                                );
                                checkItemBiddingEndedTimerDisposer = executeAt(
                                    () => checkItemBiddingEnded(),
                                    presenter.earliestItemEndTime
                                );
                            }

                            const earliestItemChangedDisposer = reaction(
                                () => presenter.earliestItemEndTime,
                                () => {
                                    logger.log(
                                        `[${new Date().toISOString()}] earliestItemEndTime changed`,
                                        new Date(presenter.earliestItemEndTime).toISOString()
                                    );
                                    checkItemBiddingEndedTimerDisposer?.clearTimeout();
                                    checkItemBiddingEndedTimerDisposer = executeAt(
                                        () => checkItemBiddingEnded(),
                                        presenter.earliestItemEndTime
                                    );
                                }
                            );

                            const createAuctionCountDown = () => {
                                auctionCountDownDisposer && auctionCountDownDisposer();
                                return startCountDown(presenter, presenter.endAuctionTime);
                            };

                            const checkAuctionBiddingEnded = (at: number) => {
                                logger.log(`[${new Date().toISOString()}] scheduling checkAuctionBiddingEnded`);
                                const disposer = executeAt(() => callback('CHECK_BIDDING_ENDED'), at);
                                if (!disposer) {
                                    callback('CHECK_BIDDING_ENDED');
                                }
                                return disposer;
                            };

                            const checkItemBiddingEnded = () => {
                                checkItemBiddingEndedPollingDisposer?.();
                                checkItemBiddingEndedPollingDisposer = checkItemBiddingEndedPolling();
                            };

                            const checkItemBiddingEndedPolling = () => {
                                const winningBid = presenter.earliestWinningBid;
                                if (!winningBid || winningBid.ended) {
                                    return () => {};
                                }
                                logger.log(
                                    `[${new Date().toISOString()}] start checking item bidding ended for`,
                                    winningBid.itemKey
                                );

                                const interval = setInterval(() => {
                                    const winningBid = presenter.earliestWinningBid;
                                    if (!winningBid || winningBid.ended) {
                                        clearInterval(interval);
                                        return;
                                    }
                                    logger.log(
                                        `[${new Date().toISOString()}] check item bidding ended interval`,
                                        winningBid.itemKey,
                                        winningBid.ended
                                    );
                                    const {auction} = presenter;
                                    services.bidService
                                        .checkItemBiddingEnded(auction.regionId, auction.auctionId, winningBid.itemKey)
                                        .catch(logError('Failed to check item bidding ended'));
                                }, CHECK_ITEM_BIDDING_ENDED_INTERVAL);

                                return () => {
                                    logger.log(
                                        `[${new Date().toISOString()}] Clearing check item bidding ended interval`,
                                        winningBid.itemKey
                                    );
                                    clearInterval(interval);
                                };
                            };

                            const latestItemChangedDisposer = reaction(
                                () => presenter.latestItemEndTime,
                                () => {
                                    logger.log(
                                        `[${new Date().toISOString()}] latestItemEndTime changed`,
                                        new Date(presenter.latestItemEndTime).toISOString()
                                    );
                                    checkBiddingEndedTimerDisposer?.clearTimeout();
                                    checkBiddingEndedTimerDisposer = checkAuctionBiddingEnded(
                                        presenter.latestItemEndTime
                                    );
                                }
                            );

                            const endAuctionTimeChangedDisposer = reaction(
                                () => presenter.endAuctionTime,
                                () => {
                                    logger.log(
                                        `[${new Date().toISOString()}] endAuctionTime changed`,
                                        new Date(presenter.endAuctionTime).toISOString()
                                    );
                                    auctionCountDownDisposer && auctionCountDownDisposer();
                                    auctionCountDownDisposer = createAuctionCountDown();
                                }
                            );

                            checkBiddingEndedTimerDisposer = checkAuctionBiddingEnded(presenter.latestItemEndTime);
                            auctionCountDownDisposer = createAuctionCountDown();

                            return () => {
                                earliestItemChangedDisposer();
                                latestItemChangedDisposer();
                                endAuctionTimeChangedDisposer();
                                checkBiddingEndedTimerDisposer?.clearTimeout();
                                auctionCountDownDisposer?.();
                                checkItemBiddingEndedTimerDisposer?.clearTimeout();
                                checkItemBiddingEndedPollingDisposer?.();
                            };
                        },
                },
                on: {
                    CHECK_BIDDING_ENDED: ListingAuctionStateEnum.AFTER_BIDDING,
                    START_BIDDING: ListingAuctionStateEnum.BIDDING,
                },
            },
            afterBidding: {
                entry: ['checkAuctionBiddingEnded'],
                invoke: {
                    src:
                        ({presenter}) =>
                        (callback) => {
                            logger.log('After Bidding State Entered', {
                                earliestItemEndTime: new Date(presenter.earliestItemEndTime).toISOString(),
                                latestItemEndTime: new Date(presenter.latestItemEndTime).toISOString(),
                            });
                            if (!presenter.latestWinningBid) {
                                const {auction} = presenter;
                                if (auction.settings.isContinuousAuctionEnabled && !auction.ended) {
                                    callback('START_BIDDING');
                                } else {
                                    callback('ENDED_RECENTLY');
                                }
                                return;
                            }
                            if (presenter.latestWinningBid.ended) {
                                callback('START_BIDDING');
                                return;
                            }

                            const earliestTimeChangedDisposer = reaction(
                                () => presenter.latestItemEndTime,
                                () => callback('START_BIDDING')
                            );
                            const winningBidEndedDisposer = when(
                                () => Boolean(presenter.latestWinningBid?.ended),
                                () => callback('START_BIDDING')
                            );

                            return () => {
                                earliestTimeChangedDisposer();
                                winningBidEndedDisposer();
                            };
                        },
                },
                on: {
                    START_BIDDING: [
                        {target: ListingAuctionStateEnum.ENDED_RECENTLY, cond: 'isAuctionEnded'},
                        {target: ListingAuctionStateEnum.BIDDING},
                    ],
                    ENDED_RECENTLY: ListingAuctionStateEnum.ENDED_RECENTLY,
                },
            },
            suspended: {
                invoke: {
                    src: pauseStateInvocation,
                },
                on: {
                    RESUME: [{target: ListingAuctionStateEnum.BIDDING}],
                },
            },
            endedRecently: {
                invoke: {
                    src:
                        ({presenter: {endAuctionTime, clock}}: ListingAuctionStateMachineContext) =>
                        (callback: Sender<ListingAuctionStateMachineEvent>) => {
                            let timerDisposer: LongTimeoutResult | undefined = undefined;

                            const checkEndedRecentlyStateEnded = () => {
                                timerDisposer && timerDisposer.clearTimeout();

                                const now = Date.now();
                                const timeAfterEnd = now - endAuctionTime;
                                const time = now + (ENDED_RECENTLY_TIME_IN_MS - timeAfterEnd);

                                const disposer = executeAt(() => callback('END_AUCTION'), time);

                                if (!disposer) {
                                    callback('END_AUCTION');
                                } else {
                                    logger.log('Ended recently status will be changed at', new Date(time));
                                }

                                return disposer;
                            };

                            timerDisposer = checkEndedRecentlyStateEnded();

                            const clockOffsetChangeDisposer = subscribeToClockOffsetChange(clock)(() => {
                                timerDisposer = checkEndedRecentlyStateEnded();
                            });

                            return () => {
                                timerDisposer && timerDisposer.clearTimeout();
                                clockOffsetChangeDisposer();
                            };
                        },
                    onError: {
                        target: ListingAuctionStateEnum.ERROR,
                    },
                },
                on: {
                    END_AUCTION: ListingAuctionStateEnum.ENDED,
                },
            },
            ended: {
                type: 'final',
            },
            error: {
                type: 'final',
                invoke: {
                    id: 'error',
                    src: (context, event) => () => {
                        logger.log('ERROR: Auction is in invalid state.', event);
                    },
                },
            },
            empty: {
                type: 'final',
            },
        },
    },
    {
        guards: {
            isSuspended: ({presenter}) => {
                return presenter.auction.isSuspended;
            },
            isAuctionStarted: ({presenter}) => {
                return presenter.clock.parseDate(presenter.auction.settings.start) < Date.now();
            },
            isAuctionEnded: ({presenter: {latestWinningBid, endAuctionTime}}) => {
                return !!latestWinningBid?.ended || Date.now() >= endAuctionTime;
            },
            isEmpty: ({presenter: {isEmpty}}) => isEmpty,
        },
        actions: {
            checkAuctionBiddingEnded: ({services, presenter: {auction}}) => {
                services.bidService.checkAuctionBiddingEnded(auction.regionId, auction.auctionId).catch(logError());
            },
        },
    }
);
